import React, { useState, useRef } from 'react';
import { FileText, Upload, Search, Trash2, File, Image, FileSpreadsheet, Eye, Download, X } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Card, PageHeader } from '../components/Card';

const DOC_CATEGORIES = ['All Categories', 'Pre-Approval', 'Offer', 'Inspection', 'Legal', 'Insurance', 'Closing', 'Other'];

function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function getFileIcon(type) {
  if (type && type.startsWith('image/')) return <Image size={20} color="#3b82f6" />;
  if (type && (type.includes('spreadsheet') || type.includes('csv') || type.includes('excel'))) return <FileSpreadsheet size={20} color="#2d8a4e" />;
  if (type && type.includes('pdf')) return <FileText size={20} color="#dc3545" />;
  return <File size={20} color="var(--primary)" />;
}

export default function Documents() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All Categories');
  const [uploadCategory, setUploadCategory] = useState('Other');
  const [previewDoc, setPreviewDoc] = useState(null);
  const { documents, setDocuments } = useApp();
  const fileInputRef = useRef(null);

  const filtered = documents.filter(d => {
    const matchSearch = d.name.toLowerCase().includes(search.toLowerCase());
    const matchCategory = category === 'All Categories' || d.category === category;
    return matchSearch && matchCategory;
  });

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setDocuments(prev => [...prev, {
          id: Date.now().toString() + Math.random().toString(36).slice(2),
          name: file.name,
          category: uploadCategory,
          date: new Date().toISOString().split('T')[0],
          size: formatFileSize(file.size),
          sizeBytes: file.size,
          type: file.type,
          dataUrl: ev.target.result,
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      setDocuments(prev => prev.filter(d => d.id !== id));
    }
  };

  const handleDownload = (doc) => {
    if (doc.dataUrl) {
      const link = document.createElement('a');
      link.href = doc.dataUrl;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const canPreview = (doc) => {
    return doc.type && (doc.type.startsWith('image/') || doc.type === 'application/pdf');
  };

  return (
    <div>
      <PageHeader
        icon={<FileText size={22} />}
        title="Documents"
        subtitle="Upload and organize your home buying documents"
        action={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select
              value={uploadCategory}
              onChange={e => setUploadCategory(e.target.value)}
              style={{
                padding: '10px 12px', border: '1px solid var(--gray-300)',
                borderRadius: 'var(--radius-sm)', fontSize: 14, background: 'white',
              }}
            >
              {DOC_CATEGORIES.filter(c => c !== 'All Categories').map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 18px', borderRadius: 'var(--radius-sm)',
                background: 'var(--primary)', color: 'white',
                fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap',
              }}
            >
              <Upload size={16} /> Upload
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.txt,.rtf"
            />
          </div>
        }
      />

      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, position: 'relative', minWidth: 200 }}>
            <Search size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)' }} />
            <input
              type="text" placeholder="Search documents..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '10px 14px 10px 38px',
                border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)',
                fontSize: 14,
              }}
            />
          </div>
          <select
            value={category} onChange={e => setCategory(e.target.value)}
            style={{
              padding: '10px 14px', border: '1px solid var(--gray-300)',
              borderRadius: 'var(--radius-sm)', fontSize: 14, background: 'white',
              minWidth: 160,
            }}
          >
            {DOC_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </Card>

      {documents.length > 0 && (
        <div style={{
          display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap',
        }}>
          <Card style={{ flex: 1, minWidth: 140, textAlign: 'center', padding: '16px 12px' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--primary)' }}>{documents.length}</div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Total Files</div>
          </Card>
          <Card style={{ flex: 1, minWidth: 140, textAlign: 'center', padding: '16px 12px' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--primary)' }}>
              {new Set(documents.map(d => d.category)).size}
            </div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Categories Used</div>
          </Card>
          <Card style={{ flex: 1, minWidth: 140, textAlign: 'center', padding: '16px 12px' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--primary)' }}>
              {formatFileSize(documents.reduce((sum, d) => sum + (d.sizeBytes || 0), 0))}
            </div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Total Size</div>
          </Card>
        </div>
      )}

      {filtered.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 48 }}>
          <Upload size={48} color="var(--gray-300)" style={{ marginBottom: 16 }} />
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: 'var(--gray-500)' }}>
            {documents.length === 0 ? 'No documents yet' : 'No matching documents'}
          </h3>
          <p style={{ color: 'var(--gray-400)', fontSize: 14, marginBottom: 20 }}>
            {documents.length === 0
              ? 'Upload your first document to get started. Select a category above, then click Upload.'
              : 'Try adjusting your search or category filter.'}
          </p>
          {documents.length === 0 && (
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '12px 24px', borderRadius: 'var(--radius-sm)',
                background: 'var(--primary)', color: 'white',
                fontWeight: 600, fontSize: 14,
              }}
            >
              <Upload size={16} /> Upload Your First Document
            </button>
          )}
        </Card>
      ) : (
        <div>
          {filtered.map(doc => (
            <Card key={doc.id} style={{
              marginBottom: 8, display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', padding: '14px 20px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                {getFileIcon(doc.type)}
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 500, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                    {doc.category} &middot; {doc.date} &middot; {doc.size}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                {canPreview(doc) && (
                  <button
                    onClick={() => {
                      if (doc.type === 'application/pdf') {
                        const w = window.open('', '_blank');
                        w.document.write(`<html><head><title>${doc.name}</title></head><body style="margin:0"><embed src="${doc.dataUrl}" type="application/pdf" width="100%" height="100%" style="position:absolute;inset:0" /></body></html>`);
                      } else {
                        setPreviewDoc(doc);
                      }
                    }}
                    title="Preview"
                    style={{ background: 'none', color: 'var(--gray-500)', padding: 6 }}
                  >
                    <Eye size={16} />
                  </button>
                )}
                {doc.dataUrl && (
                  <button
                    onClick={() => handleDownload(doc)}
                    title="Download"
                    style={{ background: 'none', color: 'var(--gray-500)', padding: 6 }}
                  >
                    <Download size={16} />
                  </button>
                )}
                <button
                  onClick={() => handleDelete(doc.id)}
                  title="Delete"
                  style={{ background: 'none', color: 'var(--gray-400)', padding: 6 }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {previewDoc && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 300,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
          }}
          onClick={() => setPreviewDoc(null)}
        >
          <div
            style={{
              background: 'white', borderRadius: 12,
              maxWidth: '90vw', maxHeight: '90vh',
              overflow: 'auto', position: 'relative',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 16px', borderBottom: '1px solid var(--gray-200)',
            }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{previewDoc.name}</span>
              <button
                onClick={() => setPreviewDoc(null)}
                style={{ background: 'none', color: 'var(--gray-500)', padding: 4 }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: 16 }}>
              {previewDoc.type?.startsWith('image/') ? (
                <img
                  src={previewDoc.dataUrl}
                  alt={previewDoc.name}
                  style={{ maxWidth: '100%', maxHeight: '70vh', display: 'block', margin: '0 auto' }}
                />
              ) : previewDoc.type === 'application/pdf' ? (
                <iframe
                  src={previewDoc.dataUrl}
                  title={previewDoc.name}
                  style={{ width: '100%', minWidth: 300, height: '70vh', border: 'none' }}
                />
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
